import {
  AmountType,
  CapacityCategory,
  CostType,
} from 'src/apis/monolith/types';
import { DirectionType } from 'src/components/data-display/direction';
import { NetworkPointSuggestion } from 'src/components/domain-specifics/network-points/nwp-multi-select';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useSearchParams } from 'src/hooks/use-search-params';
import { PeriodTypeValue } from 'src/pages/reporting/auctions/components/period-type';
import { TsoSuggestion } from 'src/pages/reporting/my-transactions/tso-suggestions-input';
import { AuctionPhaseType } from 'src/pages/reporting/phase';
import { isValidDateQuery } from 'src/utils/is-valid-date';
import * as pp from 'src/utils/page-params';

const basicParams = () =>
  pp
    .configure()
    .add(pp.param('periodType').default<PeriodTypeValue>('WITHINDAY'))
    .add(pp.number('slot').default(1))
    .add(
      pp
        .param('runtime-from')
        .deserialize((value) =>
          value === null ? null : isValidDateQuery(value, 'runtime-from')
        )
    )
    .add(
      pp
        .param('runtime-to')
        .deserialize((value) =>
          value === null ? null : isValidDateQuery(value, 'runtime-to')
        )
    )
    .add(
      pp
        .param('identifier')
        // old angular pages send an empty string instead of no `identifier`
        // at all, but APIs expect either null or a valid identifier
        .deserialize((value) => (value === '' ? null : value))
    );

export function useInitialPageParams() {
  return pp.usePageParams(basicParams());
}

export function usePageParams(options: { defaultToFavoriteFilter: boolean }) {
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const hasAuctionId = useSearchParams().has('auctionId');

  return pp.usePageParams(
    basicParams()
      .add(pp.start('start'))
      .add(pp.number('pageSize').default(10))
      .add(pp.param('phase').default<AuctionPhaseType>('OPEN'))
      .add(pp.param('runtimeFromFilter').filter({ label: 'Product Runtime' }))
      .add(pp.param('runtimeToFilter').filter({ label: 'Product Runtime' }))
      .add(pp.param('customQuickFilter').filter({ label: 'Quick Filter' }))
      .add(pp.optionalNumber('marketable-min').filter({ label: 'Marketable' }))
      .add(pp.optionalNumber('marketable-max').filter({ label: 'Marketable' }))
      .add(
        pp
          .optionalNumber('startingPrice-min')
          .filter({ label: 'Starting Price' })
      )
      .add(
        pp
          .optionalNumber('startingPrice-max')
          .filter({ label: 'Starting Price' })
      )
      .add(
        pp
          .list('capacityCategory')
          .cast<CapacityCategory>()
          .filter({ label: 'Capacity Category' })
      )
      .add(
        pp
          .list('tso')
          .deserialize<TsoSuggestion>(JSON.parse)
          .serialize(JSON.stringify)
          .filter({ label: 'TSO' })
      )
      .add(
        pp
          .list('direction')
          .cast<DirectionType>()
          .filter({ label: 'Direction' })
      )
      .add(
        pp
          .param('costSetting')
          .default<CostType>('SUBUNIT')
          .filter({ label: 'Currency' })
      )
      .add(
        pp
          .param('amountSetting')
          .default<AmountType>('KILO')
          .filter({ label: 'Capacity' })
      )
      .add(pp.boolean('sortAscending').default(true))
      .add(pp.param('sortColumn').default('networkPointName'))
      .add(pp.param('category'))
      .add(
        pp
          .boolean('favoritesOnly')
          .default(options.defaultToFavoriteFilter)
          .filter({ label: 'Quick Filter' })
      )
      .add(
        pp
          .boolean('ownTsoOnly')
          .default(!hasAuctionId && Boolean(monolithUser?.isTso))
          .filter({ label: 'Quick Filter' })
      )
      .add(
        pp
          .list('networkPoint')
          .deserialize<NetworkPointSuggestion>(JSON.parse)
          .serialize(JSON.stringify)
          .filter({ label: 'Network Point' })
      )
      .add(pp.list('networkPointQuickFilter'))
      .add(pp.param('auctionId').filter({ label: 'Auction ID' }))
  );
}

export type PageParams = ReturnType<typeof usePageParams>;
